import { PureComponent, ReactNode } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import { MainStoreState } from 'modules/main/store';
import {
  hideModal as storeHideModal,
  showModal as storeShowModal,
} from 'modules/main/store/ducks/modal';
import { configValue } from 'utils/config';
import { deleteCookie, getCookieValue } from 'utils/cookies';
import { inNoninterruptibleUserFlow } from 'utils/intakeintents';
import { log } from 'utils/logging';
import { RouteComponentProps, withRouter } from 'utils/withRouter';

type OwnProps = {
  children: ReactNode;
};
type Props = OwnProps & RouteComponentProps & PropsFromRedux;

class UnconnectedShowUserModals extends PureComponent<Props> {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/sort-comp
  verify() {
    const { userEnvironment, location, modal, showModal, hideModal } =
      this.props;
    const verifyEmail =
      userEnvironment.user &&
      Boolean(userEnvironment.user.email) &&
      !userEnvironment.user.emailVerified;

    // Verify Email

    if (
      verifyEmail &&
      !modal.type &&
      location.pathname === '/salaries/explore'
    ) {
      return;
    }

    if (verifyEmail && !getCookieValue('justSignedUpModal')) {
      // INFORMATION_REQUIRED expected for case of Facebook user not allowing
      // their email address and then has just filled one in.
      if (
        !modal.type ||
        ['GRAD_PROGRAM_CONTACT', 'INFORMATION_REQUIRED'].includes(modal.type)
      ) {
        // only show the modal if another isn't already shown
        showModal('VERIFY_EMAIL', {});
      }

      return;
    }

    if (modal.type === 'VERIFY_EMAIL') {
      hideModal();
    }

    // OAuth

    if (
      getCookieValue('justSignedUpModal') &&
      (!modal.type || modal.type === 'GRAD_PROGRAM_CONTACT')
    ) {
      const method = getCookieValue('justSignedUpModal');

      if (
        method === 'Google' ||
        method === 'Facebook' ||
        method === '"Apple ID"' // NOTE: needs double quotes to work
      ) {
        showModal('FINISH_OAUTH_SIGNUP', {
          onClose: () => deleteCookie('justSignedUpModal'),
        });
      } else {
        deleteCookie('justSignedUpModal');
      }

      return;
    }

    // Information Required

    const informationRequired =
      userEnvironment.user &&
      (!userEnvironment.user.firstName ||
        !userEnvironment.user.lastName ||
        !userEnvironment.user.email) &&
      location.pathname !== '/dashboard/account-settings';

    if (informationRequired) {
      if (!modal.type || modal.type === 'GRAD_PROGRAM_CONTACT') {
        // only show the modal if another isn't already shown
        showModal('INFORMATION_REQUIRED', {});
      }

      return;
    }

    if (modal.type === 'INFORMATION_REQUIRED') {
      hideModal();
    }

    // iOS app needs log in
    const iOSAppNeedsLogIn =
      window.navigator.userAgent.includes('Idealist Mobile App') &&
      userEnvironment.isUserLoaded &&
      !userEnvironment.user &&
      location.pathname !== '/login' &&
      location.pathname !== '/signup';

    if (iOSAppNeedsLogIn) {
      if (!modal.type) {
        // only show the modal if another isn't already shown
        showModal('SIGNUP', { uncloseable: true });
      }

      return;
    }

    // Notification Preferences

    if (userEnvironment.user?.showNotifPrefModal) {
      if (!modal.type) {
        // only show the modal if another isn't already shown
        showModal('NOTIFICATION_PREFERENCES', {});
      }

      return;
    }

    if (modal.type === 'NOTIFICATION_PREFERENCES') {
      hideModal();
    }

    if (
      userEnvironment.isUserLoaded &&
      userEnvironment.user &&
      !userEnvironment.user.intakeIntentsDatetimeSet &&
      !modal.type &&
      !inNoninterruptibleUserFlow(location.pathname)
    ) {
      let intakeIntentsStartDateConfig = configValue(
        'idealist',
        'intakeIntentsStartDate',
      );
      if (!intakeIntentsStartDateConfig) {
        log.error('idealist.intake_intents_start_date config variable not set');
        intakeIntentsStartDateConfig = '2023-11-01';
      }
      const intakeIntentsStartDate = new Date(intakeIntentsStartDateConfig);
      if (new Date(userEnvironment.user.created) > intakeIntentsStartDate) {
        showModal('INTAKE_INTENTS', {});
      }
    }
  }

  componentDidMount() {
    this.verify();
  }

  componentDidUpdate() {
    this.verify();
  }

  render() {
    const { children } = this.props;

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }
}

const mapStateToProps = ({ userEnvironment, modal }: MainStoreState) => ({
  userEnvironment,
  modal,
});

const connector = connect(mapStateToProps, {
  hideModal: storeHideModal,
  showModal: storeShowModal,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ShowUserModals = withRouter(connector(UnconnectedShowUserModals));
